/**
 * Safe wrappers for localStorage and sessionStorage.
 * iOS Safari Private Browsing throws QuotaExceededError on write
 * and may throw on read in older versions. These wrappers fall back
 * to an in-memory Map so the app never crashes.
 */

const mem = new Map<string, string>();

export const safeLocalStorage = {
  getItem(key: string): string | null {
    try { return localStorage.getItem(key); }
    catch { return mem.get(`l:${key}`) ?? null; }
  },
  setItem(key: string, value: string): void {
    try { localStorage.setItem(key, value); }
    catch { mem.set(`l:${key}`, value); }
  },
  removeItem(key: string): void {
    try { localStorage.removeItem(key); }
    catch { mem.delete(`l:${key}`); }
  },
};

export const safeSessionStorage = {
  getItem(key: string): string | null {
    try { return sessionStorage.getItem(key); }
    catch { return mem.get(`s:${key}`) ?? null; }
  },
  setItem(key: string, value: string): void {
    try { sessionStorage.setItem(key, value); }
    catch { mem.set(`s:${key}`, value); }
  },
  removeItem(key: string): void {
    try { sessionStorage.removeItem(key); }
    catch { mem.delete(`s:${key}`); }
  },
};

/** Copy text to clipboard with fallback for older iOS Safari */
export async function safeClipboardWrite(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback: hidden textarea + execCommand (works on iOS 10+)
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;opacity:0;left:-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch { /* ignore */ }
    document.body.removeChild(ta);
    return true;
  }
}
